import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAnalyticsDates } from '@/modules/analytics/redux/selectors';
import { isSingleDayRange } from '@/modules/analytics/utils/formatDateRangeLabel';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';

import { useAiAgentsVariables } from './useAiAgentsVariables';
import { useComparisonMode } from '../../../hooks/useComparisonMode';
import type { CsatBreakdown, TimeseriesDataPoint } from '../../../hooks/useOverviewTimeseries';
import type { AiAgentMetricKey } from '../aiAgentsInsightsConfig';

interface AiAgentKpiValues {
  current: number;
  previous: number;
}

export interface AiAgentsInsightsData {
  kpiValues: Record<AiAgentMetricKey, AiAgentKpiValues>;
  csatBreakdown: CsatBreakdown;
  comparisonCsatBreakdown: CsatBreakdown;
  timeseries: Record<AiAgentMetricKey, TimeseriesDataPoint[]>;
  comparisonTimeseries: Record<AiAgentMetricKey, TimeseriesDataPoint[]>;
}

interface AiAgentsOverviewRow {
  total_conversations: number | null;
  containment_rate: number | null;
  avg_response_time: number | null;
  /**
   * Pooled CSAT: AI-generated scores and end-user ratings averaged together,
   * count-weighted. This is the headline number, computed by the same formula as
   * `overview_session_metrics.avg_ai_csat`, so both pages report one value.
   * `avg_csat_brain` / `avg_user_rating` remain the components behind it.
   */
  avg_ai_csat: number | null;
  avg_csat_brain: number | null;
  avg_user_rating: number | null;
  csat_brain_count: number | null;
  user_rating_count: number | null;
}

export interface AiAgentsTimeseriesRow {
  time_bucket: string;
  total_conversations: number | null;
  not_contained_count: number | null;
  meaningful_count: number | null;
  avg_response_time: number | null;
  user_rating_sum: number | null;
  user_rating_count: number | null;
  csat_brain_sum: number | null;
  csat_brain_count: number | null;
}

const OVERVIEW_FIELDS = `
  total_conversations
  containment_rate
  avg_response_time
  avg_ai_csat
  avg_csat_brain
  avg_user_rating
  csat_brain_count
  user_rating_count
`;

const TIMESERIES_FIELDS = `
  time_bucket
  total_conversations
  not_contained_count
  meaningful_count
  avg_response_time
  user_rating_sum
  user_rating_count
  csat_brain_sum
  csat_brain_count
`;

// Distinct operation names so Apollo's cache treats current and comparison
// queries as separate (same shape, different time window).
const GET_AI_AGENTS_CONVERSATIONS_SUMMARY = gql`
  query AiAgentsConversationsSummary(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_CONVERSATIONS_SUMMARY_COMPARISON = gql`
  query AiAgentsConversationsSummaryComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_summary(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${OVERVIEW_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_CONVERSATIONS_TIMESERIES = gql`
  query AiAgentsConversationsTimeseries(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const GET_AI_AGENTS_CONVERSATIONS_TIMESERIES_COMPARISON = gql`
  query AiAgentsConversationsTimeseriesComparison(
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
    $deskIds: _uuid
    $channels: _text
    $isProactive: Boolean
    $csatBrain: _int4
    $isTest: Boolean
  ) {
    rows: ai_agents_conversations_timeseries(
      args: {
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
        desk_ids: $deskIds
        channels: $channels
        is_proactive: $isProactive
        csat_brain: $csatBrain
        is_test: $isTest
      }
    ) {
      ${TIMESERIES_FIELDS}
    }
  }
`;

const EMPTY_KPI: AiAgentKpiValues = { current: 0, previous: 0 };

const EMPTY_KPI_VALUES: Record<AiAgentMetricKey, AiAgentKpiValues> = {
  csat: EMPTY_KPI,
  containment: EMPTY_KPI,
  responseTime: EMPTY_KPI,
  totalConversations: EMPTY_KPI,
};

const EMPTY_CSAT_BREAKDOWN: CsatBreakdown = {
  avgUserRating: null,
  userRatingCount: 0,
  avgAiInsights: null,
  aiInsightsCount: 0,
};

const round1 = (value: number): number => Number(value.toFixed(1));

const emptyTimeseriesRecord = (): Record<AiAgentMetricKey, TimeseriesDataPoint[]> => ({
  csat: [],
  containment: [],
  responseTime: [],
  totalConversations: [],
});

export const buildTimeseriesByMetric = (
  rows: AiAgentsTimeseriesRow[] | undefined
): Record<AiAgentMetricKey, TimeseriesDataPoint[]> => {
  const result = emptyTimeseriesRecord();
  if (!rows?.length) {
    return result;
  }

  for (const row of rows) {
    const date = row.time_bucket;

    // Pool AI-generated scores and end-user ratings into one count-weighted
    // average, the same way the backend builds the period-level `avg_ai_csat`.
    // A day with only one kind of review still plots — it just has one component.
    const brainCount = row.csat_brain_count ?? 0;
    const userCount = row.user_rating_count ?? 0;
    const pooledCount = brainCount + userCount;
    if (pooledCount > 0) {
      result.csat.push({
        date,
        value: round1(((row.csat_brain_sum ?? 0) + (row.user_rating_sum ?? 0)) / pooledCount),
        // Per-day review split for the tooltip; a null component means the day
        // had no reviews of that kind (rendered as "–", not a fake 0.0).
        csatBreakdown: {
          avgUserRating: userCount > 0 ? round1((row.user_rating_sum ?? 0) / userCount) : null,
          userRatingCount: userCount,
          avgAiInsights: brainCount > 0 ? round1((row.csat_brain_sum ?? 0) / brainCount) : null,
          aiInsightsCount: brainCount,
        },
      });
    }

    const meaningful = row.meaningful_count ?? 0;
    const containmentRate = meaningful > 0 ? 1 - (row.not_contained_count ?? 0) / meaningful : null;
    if (containmentRate != null) {
      result.containment.push({ date, value: Math.round(containmentRate * 100) });
    }

    if (row.avg_response_time != null) {
      // Backend returns response time in minutes; the chart and KPI work in seconds.
      result.responseTime.push({ date, value: row.avg_response_time * 60 });
    }

    if (row.total_conversations != null) {
      result.totalConversations.push({ date, value: row.total_conversations, containmentRate });
    }
  }

  return result;
};

const toCsatBreakdown = (row: AiAgentsOverviewRow | null): CsatBreakdown => {
  if (!row) {
    return EMPTY_CSAT_BREAKDOWN;
  }
  // Round to the display precision so every consumer (score, compare delta,
  // export) works from the same value — a raw 4.44 vs 4.36 would render "4.4"
  // on both periods yet report a "+0.1" delta. A component with no reviews is
  // null (rendered as "–"), matching the per-day breakdowns — never a fake 0.0.
  const userCount = row.user_rating_count ?? 0;
  const brainCount = row.csat_brain_count ?? 0;
  return {
    avgUserRating: userCount > 0 ? round1(row.avg_user_rating ?? 0) : null,
    userRatingCount: userCount,
    avgAiInsights: brainCount > 0 ? round1(row.avg_csat_brain ?? 0) : null,
    aiInsightsCount: brainCount,
  };
};

export const useAiAgentsInsights = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);

  // Mirror the Overview page: skip the timeseries query on single-day ranges
  // (the chart isn't rendered for those, so the round-trip is wasted).
  const isSingleDay = isSingleDayRange(startDate, endDate);

  const { variables: currentVariables, skip } = useAiAgentsVariables();
  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: comparisonVariables } = useAiAgentsVariables(comparisonDates);

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError,
  } = useQuery<{ rows: AiAgentsOverviewRow[] }>(GET_AI_AGENTS_CONVERSATIONS_SUMMARY, {
    variables: currentVariables,
    skip,
  });

  const {
    data: comparisonData,
    loading: comparisonLoading,
    error: comparisonError,
  } = useQuery<{ rows: AiAgentsOverviewRow[] }>(GET_AI_AGENTS_CONVERSATIONS_SUMMARY_COMPARISON, {
    variables: comparisonVariables,
    skip: skip || !isComparing,
  });

  const {
    data: timeseriesData,
    loading: timeseriesLoading,
    error: timeseriesError,
  } = useQuery<{ rows: AiAgentsTimeseriesRow[] }>(GET_AI_AGENTS_CONVERSATIONS_TIMESERIES, {
    variables: currentVariables,
    skip: skip || isSingleDay,
  });

  const {
    data: comparisonTimeseriesData,
    loading: comparisonTimeseriesLoading,
    error: comparisonTimeseriesError,
  } = useQuery<{ rows: AiAgentsTimeseriesRow[] }>(
    GET_AI_AGENTS_CONVERSATIONS_TIMESERIES_COMPARISON,
    { variables: comparisonVariables, skip: skip || !isComparing || isSingleDay }
  );

  const currentRow = currentData?.rows?.[0] ?? null;
  const comparisonRow = comparisonData?.rows?.[0] ?? null;

  const isEmpty = !currentRow || (currentRow.total_conversations ?? 0) === 0;

  const timeseriesByMetric = useMemo(
    () => buildTimeseriesByMetric(timeseriesData?.rows),
    [timeseriesData?.rows]
  );

  const comparisonTimeseriesByMetric = useMemo(
    () => buildTimeseriesByMetric(comparisonTimeseriesData?.rows),
    [comparisonTimeseriesData?.rows]
  );

  const data = useMemo<AiAgentsInsightsData>(() => {
    const kpiValues: Record<AiAgentMetricKey, AiAgentKpiValues> = {
      csat: {
        current: round1(currentRow?.avg_ai_csat ?? 0),
        previous: round1(comparisonRow?.avg_ai_csat ?? 0),
      },
      containment: {
        current: Math.round((currentRow?.containment_rate ?? 0) * 100),
        previous: Math.round((comparisonRow?.containment_rate ?? 0) * 100),
      },
      responseTime: {
        // Backend returns minutes; convert to seconds so consumers can format directly.
        current: (currentRow?.avg_response_time ?? 0) * 60,
        previous: (comparisonRow?.avg_response_time ?? 0) * 60,
      },
      totalConversations: {
        current: currentRow?.total_conversations ?? 0,
        previous: comparisonRow?.total_conversations ?? 0,
      },
    };

    return {
      kpiValues: currentRow ? kpiValues : EMPTY_KPI_VALUES,
      csatBreakdown: toCsatBreakdown(currentRow),
      comparisonCsatBreakdown: toCsatBreakdown(comparisonRow),
      timeseries: timeseriesByMetric,
      comparisonTimeseries: comparisonTimeseriesByMetric,
    };
  }, [currentRow, comparisonRow, timeseriesByMetric, comparisonTimeseriesByMetric]);

  return {
    data,
    isLoading:
      currentLoading ||
      timeseriesLoading ||
      (isComparing && (comparisonLoading || comparisonTimeseriesLoading)),
    error: currentError ?? timeseriesError ?? comparisonError ?? comparisonTimeseriesError ?? null,
    isEmpty,
  };
};
